import { capitalize, map, size, toLower, isEmpty, chain } from "lodash"
import { useSelector } from "react-redux"
import PropTypes from "prop-types"
import classnames from "classnames"
import { List, ListItem, TextField, InputAdornment, Typography } from "@mui/material"
import { Tonality, Work, BlurOn, SelectAll, Gradient, ShoppingBasket, Search as SearchIcon } from "@mui/icons-material"
import { BASE_TYPES } from "helpers/base"
import { getIsBenchmarkEnabled } from "selectors/features"
import Result from "../result"

function getIcon(type, experimentId) {
  switch (type) {
    case BASE_TYPES.benchmark:
    case BASE_TYPES.index:
      return <Tonality />
    case BASE_TYPES.portfolio:
      return experimentId ? <BlurOn /> : <Work />
    case BASE_TYPES.etf:
      return <SelectAll />
    case BASE_TYPES.researchTopic:
      return <Gradient />
    case BASE_TYPES.swap:
      return <ShoppingBasket />
  }
}

function getSecondary(item) {
  if (!isEmpty(item.topic)) {
    return `Research topic - ${item.topic.name}`
  }
  if (item.experimentId) {
    return "Experiment"
  }
  if (item.type === BASE_TYPES.etf) {
    return item.type
  }
  return capitalize(item.type)
}

function getItem(result) {
  return {
    primary: result.name,
    secondary: getSecondary(result),
    icon: getIcon(result.type, result.experimentId),
    type: toLower(result.type),
  }
}

export default function Results({ searchResults, searchValue, setSelectedBase, portfolioBenchmark, baseHistory, baseDropdownVisible, alternateLayout, baseSearch, modelId, selectedBase }) {
  const isBenchmarkEnabled = useSelector(getIsBenchmarkEnabled)

  if (!baseDropdownVisible) { return null }

  const isSelectedBaseValid = selectedBase && (isBenchmarkEnabled || selectedBase.type !== BASE_TYPES.benchmark)
  const fallback = isSelectedBaseValid ? [selectedBase] : (isBenchmarkEnabled && portfolioBenchmark ? [portfolioBenchmark] : [])
  const history = size(baseHistory) ? baseHistory : fallback
  const results = searchValue ? searchResults : history
  const noResults = searchValue && size(searchResults) === 0

  const handleSearchChange = (event) => {
    const { value } = event.target
    baseSearch(value)
  }

  return <List className={classnames("base-search-results", { empty: noResults, "alternate-layout": alternateLayout })}>
    <ListItem className="search-text-field">
      <TextField
        variant="outlined"
        size="small"
        placeholder="Search"
        value={searchValue}
        onChange={handleSearchChange}
        autoFocus
        fullWidth
        className="base-input"
        id="base-search-input"
        slotProps={{
          input: {
            startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment>,
          },
        }}
      />
    </ListItem>
    {!searchValue && !isEmpty(history) && (
      <ListItem>
        <Typography variant="subtitle2">
          Recents
        </Typography>
      </ListItem>
    )}
    {noResults ? <ListItem>No results...</ListItem>
      : map(results, result => {
          const key = chain(result)
            .pick(["id", "experimentId", "type"])
            .map()
            .compact()
            .join("-")
            .value()
          return <Result key={key} {...getItem(result)} onClick={() => setSelectedBase(result, modelId)} />
        })}
  </List>
}

Results.propTypes = {
  searchResults: PropTypes.array.isRequired,
  searchValue: PropTypes.string.isRequired,
  setSelectedBase: PropTypes.func.isRequired,
  portfolioBenchmark: PropTypes.object.isRequired,
  selectedBase: PropTypes.object,
  baseHistory: PropTypes.array.isRequired,
  baseDropdownVisible: PropTypes.bool.isRequired,
  alternateLayout: PropTypes.bool,
  baseSearch: PropTypes.func.isRequired,
  modelId: PropTypes.string.isRequired,
}
